import { useState } from "react";
import Image from "next/image";
import {
  FacebookShareButton,
  TwitterShareButton,
  EmailShareButton,
  WhatsappShareButton,
} from "react-share";
import routes from "@/constants/routes";
import getConfig from "next/config";
import { MoveLeftIcon } from "lucide-react";
import Link from "next/link";

interface ReferralCodeProps {
  email?: string;
  onSubmit?: (email: string) => void;
  hideChangeEmail?: boolean;
}

const validateEmail = (email: string) => {
  const re = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
  return re.test(email);
};

const ReferralCode = ({
  email: initialEmail = "",
  onSubmit,
  hideChangeEmail = false,
}: ReferralCodeProps) => {
  const { publicRuntimeConfig } = getConfig();
  const referralCodeText = `${publicRuntimeConfig.NEXT_PUBLIC_URL}${routes.home}r/${
    initialEmail || "placeholder-referral-code"
  }`;
  const [isCopied, setIsCopied] = useState(false);
  const [email, setEmail] = useState(initialEmail);
  const [isEmailValid, setIsEmailValid] = useState(!!initialEmail && validateEmail(initialEmail));
  const [isChangeEmailClicked, setIsChangeEmailClicked] = useState(false);
  const handleSubmit = () => {
    if (isEmailValid && email) {
      onSubmit?.(email);
      setIsChangeEmailClicked(false);
    }
  };

  const copyToClipboard = () => {
    const dummyTextArea = document.createElement("textarea");
    dummyTextArea.value = referralCodeText;
    document.body.appendChild(dummyTextArea);
    dummyTextArea.select();
    document.execCommand("copy");
    document.body.removeChild(dummyTextArea);

    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="flex-col justify-start items-start gap-1 md:gap-2.5 inline-flex w-full">
      {!initialEmail || isChangeEmailClicked ? (
        // Show email input if no initial email
        <>
          <div className="text-stone-900 text-xl md:text-2xl font-bold leading-normal ">
            Enter your email address
          </div>
          <div className="text-stone-900 text-base font-normal leading-normal">
            And we&apos;ll give you a unique referral code
          </div>
          <div className="text-stone-900 text-base md:text-lg"></div>
          <div className="w-full flex flex-col sm:flex-row gap-4">
            <input
              type="email"
              value={email}
              onChange={(e) => {
                const value = e.target.value;
                setEmail(value);
                setIsEmailValid(validateEmail(value));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && isEmailValid) {
                  handleSubmit();
                }
              }}
              placeholder="example@email.com"
              className={`flex-1 h-13 px-4 py-2 bg-white rounded border ${
                email && !isEmailValid
                  ? "border-landing-cherry focus:ring-landing-cherry"
                  : "border-transparent"
              } focus:outline-none focus:ring-1 focus:ring-landing-orange focus:border-transparent text-gray-700 placeholder-gray-400`}
            />
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isEmailValid}
              className={`w-full sm:w-auto h-11 px-6 font-bold rounded transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                isEmailValid
                  ? "bg-landing-orange text-white hover:bg-orange-600 focus:ring-orange-500 cursor-pointer"
                  : "bg-[#cccccc] text-white hover:bg-orange-600 focus:ring-orange-500 cursor-not-allowed"
              }`}
            >
              Get your link
            </button>
          </div>
        </>
      ) : (
        // Show referral code if initial email exists
        <>
          <div className="text-stone-900 text-xl md:text-2xl font-bold leading-normal ">
            Copy your unique referral code
          </div>
          <div className="text-stone-900 text-base font-normal leading-normal">
            Send this link to someone you know and get rewarded. See our{" "}
            <Link href="/refer/terms">
              <a className="underline">Referral Terms & Conditions</a>
            </Link>
          </div>
          <div className="self-stretch justify-start items-center gap-2.5 inline-flex flex-col md:flex-row lg:flex-col xl:flex-row relative">
            <div
              className="grow shrink basis-0 h-11 p-2 md:p-4 bg-white rounded justify-between items-center gap-2.5 flex cursor-pointer relative w-full md:w-auto "
              onClick={copyToClipboard}
            >
              <div className="text-grey-550 text-base font-normal overflow-scroll whitespace-nowrap scrollBar">
                {referralCodeText}
              </div>
              <div className="w-6 h-full rounded justify-center items-center flex">
                <div className="grow shrink basis-0 self-stretch justify-center items-center inline-flex">
                  <Image src="/icons/copy.svg" width={32} height={32} alt="copy" />
                </div>
              </div>
              {isCopied && (
                <div className="absolute -top-12 right-0 bg-landing-cherry text-white text-sm p-2 rounded">
                  Copied to clipboard
                </div>
              )}
            </div>
          </div>
          {!hideChangeEmail && (
            <div
              className="flex gap-2 self-end mt-2 items-center cursor-pointer"
              onClick={() => {
                setIsChangeEmailClicked(true);
              }}
            >
              <MoveLeftIcon color="#EF7B0E" />
              <p className="font-bold text-sm text-landing-orange">Change Email</p>
            </div>
          )}
          <div>
            <p className="font-bold text-lg">Or share below</p>
            <div className="w-full flex flex-row justify-between gap-1 mt-3 md:mt-1 xl:mt-5 md:order-first">
              <WhatsappShareButton
                url={referralCodeText}
                title={"You've been referred to join Keyzy"}
              >
                <img
                  className="w-16 h-16 mr-2 hover:scale-105 transition duration-300 ease-in-out transform"
                  src="/icons/whatsapp-cherry.svg"
                  alt="whatsapp icon"
                />
              </WhatsappShareButton>

              <EmailShareButton
                url={referralCodeText}
                subject={"You've been referred to join Keyzy"}
              >
                <img
                  className="w-16 h-16 mr-2 hover:scale-105 transition duration-300 ease-in-out transform"
                  src="/icons/email-cherry.svg"
                  alt="email icon"
                />
              </EmailShareButton>

              <FacebookShareButton url={referralCodeText}>
                <img
                  className="w-16 h-16 mr-2 hover:scale-105 transition duration-300 ease-in-out transform"
                  src="/icons/fb-cherry.svg"
                  alt="facebook icon"
                />
              </FacebookShareButton>
              <TwitterShareButton url={referralCodeText} title={"Keyzy"} via="keyzyhomes">
                <img
                  className="w-16 h-16 mr-2 hover:scale-105 transition duration-300 ease-in-out transform"
                  src="/icons/x-cherry.svg"
                  alt="x icon"
                />
              </TwitterShareButton>

              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator
                      .share({
                        title: "Keyzy Referral",
                        text: "Check out Keyzy - you've been referred!",
                        url: referralCodeText,
                      })
                      .catch(console.error);
                  }
                }}
                className="border-y-landing-white p-2 w-16 h-16 flex items-center justify-center bg-cherry font-semibold text-white inline-flex items-center space-x-2 rounded-full hover:scale-105 transition duration-300 ease-in-out transform bg-white"
                aria-label="Share via native share dialog"
              >
                <svg
                  className="w-8 h-8 text-landing-cherry"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReferralCode;
